import React from 'react';
import { Link } from 'react-router-dom';

const steps = [
  {
    number: '01',
    title: 'GHG Intensity Targets Set',
    desc: 'MoEFCC notifies each obligated plant with a GHG intensity target (tonnes of CO₂e per unit of output). This is a baseline-and-credit model — not a cap. You earn credits by beating your target, and face penalties if you fall short.',
    color: 'var(--neon-green)',
  },
  {
    number: '02',
    title: 'Plants Measure & Report Emissions',
    desc: 'Each obligated entity monitors its greenhouse gas emissions throughout the compliance cycle (typically a financial year). Reporting follows prescribed MRV (Monitoring, Reporting, and Verification) protocols.',
    color: 'var(--neon-cyan)',
  },
  {
    number: '03',
    title: 'Third-Party Verification (ACVA)',
    desc: 'An Accredited Carbon Verification Agency (ACVA) independently verifies the reported emissions data. This ensures integrity and prevents gaming of the baseline.',
    color: 'var(--neon-blue)',
  },
  {
    number: '04',
    title: 'BEE Reviews & Issues CCCs',
    desc: "If a plant's verified GHG intensity is below its notified target, BEE (Bureau of Energy Efficiency) issues Carbon Credit Certificates (CCCs) proportional to the over-performance. CCCs are registered in the GCI National Registry.",
    color: 'var(--neon-orange)',
  },
  {
    number: '05',
    title: 'CCCs Are Traded on Exchanges',
    desc: 'Issued CCCs can be traded on CERC-regulated power exchanges (IEX, PXIL, HPX). Obligated entities that fall short of their targets buy CCCs from over-performers to cover their shortfall.',
    color: 'var(--neon-pink)',
  },
  {
    number: '06',
    title: 'Surrender & Compliance Settlement',
    desc: 'At the end of the compliance cycle, each obligated entity surrenders sufficient CCCs to cover any shortfall. Entities unable to surrender enough credits face an environmental compensation payment of 2× the average traded CCC price per tCO₂e shortfall.',
    color: 'var(--neon-yellow)',
  },
];

const keyTerms = [
  { term: 'CCC', def: 'Carbon Credit Certificate — the tradeable unit issued under CCTS. One CCC = 1 tonne of CO₂ equivalent reduced below target.' },
  { term: 'GHG Intensity Target', def: 'A sector-specific benchmark set for each plant. Expressed as CO₂e per unit of output (e.g., per tonne of cement produced).' },
  { term: 'ACVA', def: 'Accredited Carbon Verification Agency — an independent body that verifies emission reports.' },
  { term: 'GCI Registry', def: 'Green Credit Initiative National Registry — the central ledger where CCCs are issued, transferred, and retired.' },
  { term: 'MRV', def: 'Monitoring, Reporting, and Verification — the framework for measuring and validating emissions data.' },
  { term: 'Environmental Compensation', def: 'Penalty for non-compliance: 2× the average traded CCC price per tCO₂e shortfall, payable to a designated fund.' },
];

export default function HowCCCsWork() {
  return (
    <div className="page-shell page-stack">
      <section className="surface-panel glow-surface" style={{ padding: '1.8rem' }}>
        <div className="section-stack">
          <div className="section-tag" style={{ color: 'var(--neon-cyan)' }}>Learn</div>
          <h1 className="section-title">How Carbon Credit Certificates (CCCs) Work</h1>
          <p className="section-copy">
            A step-by-step walkthrough of India's CCTS compliance mechanism — from emission measurement to exchange trading and settlement.
          </p>
        </div>

        <div className="metric-strip" style={{ marginTop: '1.5rem' }}>
          <div className="metric-tile">
            <div className="eyebrow">Market mechanism</div>
            <div className="value" style={{ color: 'var(--neon-green)' }}>Baseline-and-credit</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">Trading unit</div>
            <div className="value" style={{ color: 'var(--neon-cyan)' }}>1 CCC = 1 tCO₂e</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">Non-compliance penalty</div>
            <div className="value" style={{ color: 'var(--neon-orange)' }}>2× avg traded price</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">Registry</div>
            <div className="value" style={{ color: 'var(--neon-pink)' }}>GCI National Registry</div>
          </div>
        </div>
      </section>

      {/* Flow steps */}
      <section className="section-stack">
        <div className="section-tag">Step-by-step flow</div>
        <h2 className="headline-sm" style={{ fontSize: '1.35rem' }}>The 6-step CCC lifecycle</h2>
        <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
          {steps.map((step) => (
            <div key={step.number} className="surface-panel" style={{ padding: '1.5rem', borderLeft: `3px solid ${step.color}`, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '1.5rem', alignItems: 'start' }}>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.6rem', color: step.color, opacity: 0.7, lineHeight: 1 }}>{step.number}</div>
              <div>
                <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--text)' }}>{step.title}</div>
                <div style={{ color: 'var(--muted)', fontSize: '0.88rem', lineHeight: '1.75' }}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Key terms */}
      <section className="section-stack">
        <div className="section-tag">Glossary</div>
        <h2 className="headline-sm" style={{ fontSize: '1.35rem' }}>Key terms explained</h2>
        <div className="section-grid-2" style={{ marginTop: '1rem' }}>
          {keyTerms.map((item) => (
            <div key={item.term} className="surface-panel" style={{ padding: '1.2rem' }}>
              <div className="stat-label" style={{ color: 'var(--neon-cyan)', marginBottom: '0.4rem' }}>{item.term}</div>
              <div style={{ color: 'var(--muted)', fontSize: '0.85rem', lineHeight: '1.7' }}>{item.def}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Navigation */}
      <section style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <Link to="/learn/compliance-vs-offset" className="btn btn-secondary">Compliance vs Offset →</Link>
        <Link to="/learn/who-can-participate" className="btn btn-secondary">Who Can Participate →</Link>
        <Link to="/market/india" className="btn btn-secondary">India Market Overview</Link>
      </section>
    </div>
  );
}
